import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { faArrowRight, faKey, faMailBulk } from "@fortawesome/free-solid-svg-icons"
import { faGoogle } from "@fortawesome/free-brands-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import logo from "../../assets/images/Logo2.png"
import image from "../../assets/images/temp.png"
import Button from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import Menu from "../../components/Menu/Menu"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { SimpleToggle } from "../../components/SimpleToggle/SimpleToggle"
import { useSignUpContext } from "../../context/useSignUpContext"
import { useError } from "../../context/useErrorContext"
import { PATH_MAP } from "../../configuration/PathMap"
import { INPUT_SIZE } from "../../configuration/InputSize"
import { useSaveSignUp } from "../../hooks/SignUp/useSaveSignUp"
import { useGoogleSignUp } from "../../hooks/SignUp/useGoogleSignUp"
import useLogin from "../../hooks/Components/useLogin"
import { setDelay } from "../../Objects/Generics/Helps"
import { normalizePhoneDigits } from "../../utils/phoneFormat"

export default function SignUpFinal() {
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const { t } = useTranslation("signup")

  const { setError } = useError()

  const { handleSingUp } = useSaveSignUp()

  const { HandleFastLogin } = useLogin()

  const { signUp, updateSignUp, handleSubmitForm } = useSignUpContext()
  
  const updateSignUpRef = useRef(updateSignUp)

  const [hasAgreedWithTerms, setHasAgreedWithTerms] = useState(true)

  const validateTerms = () => {
    if (hasAgreedWithTerms) return true

    setError(t("alert_name_terms"), t("alert_description_terms"))
    return false
  }

  const { googleButtonRef, isGoogleSignUpAvailable } = useGoogleSignUp({
    signUp,
    canSubmit: validateTerms,
  })

  useEffect(() => {
    updateSignUpRef.current = updateSignUp
  }, [updateSignUp])

  useEffect(() => {
    const email = searchParams.get("email")?.trim() ?? ""
    const phone = normalizePhoneDigits(searchParams.get("phone") ?? searchParams.get("telefone") ?? "")
    const name = searchParams.get("name")?.trim() ?? searchParams.get("nome")?.trim() ?? ""

    updateSignUpRef.current({
      ...(email ? { email } : {}),
      ...(phone ? { ownerPhone: phone } : {}),
      ...(name ? { name } : {}),
    })
  }, [searchParams])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validateTerms()) return

    const finalData = handleSubmitForm()
    finalData.isAgreedWithTerms = true

    await handleSingUp(event, finalData, async () => {
      await setDelay(200)
      await HandleFastLogin(signUp.email, signUp.password, () => navigate(PATH_MAP.COMMANDS_PAGE))
    })
  }

  return (
    <Animate type={AnimationTypes.slideRightIn} duration="0.5s">
      <div className="flex min-h-screen w-full flex-col items-center justify-center overflow-y-auto p-1">
        <Menu />

        <div className="mt-10 flex w-full justify-center px-4 pb-6 pt-4 md:mt-0 xl:min-h-[calc(100vh-80px)] xl:items-center xl:px-6">
          <section className="flex w-full max-w-6xl flex-col items-center justify-center xl:flex-row">
            <main className="flex w-full justify-center xl:w-2/5">
              <div className="flex w-full max-w-md flex-col items-center">
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <img src={logo} className="mb-5 mt-4 w-32 md:w-48" alt="Logo" />
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate}>
                  <div className="mb-4 w-full text-center">
                    <h1 className="text-2xl font-black text-slate-900">{t("title")}</h1>

                    <p className="mt-1 p-4 text-sm font-medium text-slate-600">{t("subtitle")}</p>
                  </div>
                </Animate>

                <form onSubmit={handleSubmit} className="w-full space-y-4">
                  {isGoogleSignUpAvailable && (
                    <Animate type={AnimationTypes.fadeInTraslate}>
                      <div className="group relative mx-auto h-11 w-full max-w-[400px] overflow-hidden rounded-xl shadow-md transition hover:-translate-y-0.5 hover:shadow-lg">
                        <div className="pointer-events-none absolute inset-0 z-10 flex items-center gap-3 bg-gradient-to-r from-red-600 to-orange-500 px-2 py-4 text-white">
                          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-red-500 shadow-sm">
                            <FontAwesomeIcon icon={faGoogle} />
                          </span>

                          <span className="flex-1 text-left text-sm font-black">{t("google_signup_button")}</span>

                          <FontAwesomeIcon icon={faArrowRight} />
                        </div>

                        <div
                          className="absolute inset-0 z-20 overflow-hidden opacity-[0.01] [&>div]:h-full [&>div]:w-full [&_iframe]:h-full [&_iframe]:w-full"
                          ref={googleButtonRef}
                        />
                      </div>
                    </Animate>
                  )}

                  <Animate type={AnimationTypes.fadeInTraslate}>
                    <div className="flex items-center gap-3">
                      <span className="h-px flex-1 bg-slate-200" />

                      <span className="text-xs font-bold text-slate-500">{t("email_signup_divider")}</span>

                      <span className="h-px flex-1 bg-slate-200" />
                    </div>
                  </Animate>

                  <InputField
                    id="signup-email"
                    name="email"
                    autoComplete="email"
                    icon={faMailBulk}
                    type="email"
                    value={signUp.email}
                    maxLength={INPUT_SIZE.SINGUP.mail}
                    onChange={(event) => updateSignUp({ email: event.target.value })}
                    onInvalidMessage={t("input_mail_onInvalidMessage")}
                    placeholder={t("input_mail_placeholder")}
                    required
                    descriptionKey="interactionDescriptions:pages.signUp.signUpFinal.input.mailPlaceholder"
                  />

                  <InputField
                    id="signup-password"
                    name="new-password"
                    autoComplete="new-password"
                    icon={faKey}
                    type="password"
                    value={signUp.password}
                    maxLength={INPUT_SIZE.SINGUP.password}
                    onChange={(event) => updateSignUp({ password: event.target.value })}
                    onInvalidMessage={t("input_password_onInvalidMessage")}
                    placeholder={t("input_password_placeholder")}
                    required
                    descriptionKey="interactionDescriptions:pages.signUp.signUpFinal.input.passwordPlaceholder"
                  />

                  <SimpleToggle
                    title={t("select_agree_with_terms")}
                    value={hasAgreedWithTerms}
                    boxShadow="none"
                    onChange={(value) => {
                      setHasAgreedWithTerms(value)
                      updateSignUp({ isAgreedWithTerms: value })
                    }}
                    descriptionKey="interactionDescriptions:pages.signUp.signUpFinal.toggle.selectAgreeWithTerms"
                  />

                  <Button
                    label={t("buttons:button_create_account")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor="#ef4444"
                    onClick={() => {}}
                    descriptionKey="interactionDescriptions:pages.signUp.signUpFinal.button.createAccount"
                  />
                </form>
              </div>
            </main>

            <Animate type={AnimationTypes.slideRightIn} duration="1.5s">
              <aside className="hidden justify-center xl:flex xl:w-3/5">
                <img src={image} className="w-full max-w-xl rounded-2xl shadow-lg" alt="Preview" />
              </aside>
            </Animate>
          </section>
        </div>
      </div>
    </Animate>
  )
}
